<script setup lang="ts">
import {
  LEVEL_ZERO_SKIN,
  hexAlpha,
  levelBenefits,
  levelTiers,
  mixColor,
  tierXpBounds,
} from '@/utils/levels';

const props = defineProps<{
  xp: number;
  viewIndex: number;
  playerTierIndex: number;
  /** Régua de XP indexada pelo próprio nível (`table[n]` = nível n). */
  levelXpTable: number[];
}>();

const emit = defineEmits<{ select: [index: number] }>();

const rail = ref<HTMLElement | null>(null);

/**
 * Cor do kicker "ESCALA COMPLETA": acompanha o elo em foco no swiper, como no
 * protótipo (`mix(cor, #fff, .5)`). Com o degrau "Sem elo" em foco
 * (viewIndex -1), usa o cinza dele.
 */
const kickerColor = computed(() => {
  const color =
    props.viewIndex >= 0
      ? (levelTiers()[props.viewIndex]?.color ?? LEVEL_ZERO_SKIN.color)
      : LEVEL_ZERO_SKIN.color;

  return mixColor(color, '#ffffff', 0.5);
});

const cards = computed(() => {
  // Artes resolvidas pelo CDN aqui dentro: os resolvers leem
  // `useRuntimeConfig()`, que só responde no contexto Nuxt.
  const benefits = levelBenefits();

  return levelTiers().map((tier, index) => {
    const state =
      index < props.playerTierIndex
        ? 'done'
        : index === props.playerTierIndex
          ? 'current'
          : 'locked';

    const isSelected = index === props.viewIndex;

    // Progresso dentro do elo (não dentro do nível): XP real do jogador contra
    // as fronteiras do elo na régua real. Sem piso artificial — 0% é 0%.
    const { entry, exit } = tierXpBounds(props.levelXpTable, tier);
    const span = exit - entry;

    const progress =
      state === 'current'
        ? span <= 0
          ? 100
          : Math.round(
              Math.max(0, Math.min(100, ((props.xp - entry) / span) * 100)),
            )
        : 100;

    return {
      tier,
      index,
      state,
      isSelected,
      progress,
      // Só o cartão selecionado fica colorido; os outros ficam apagados para o
      // olho achar o selecionado sem procurar.
      cardBg: isSelected
        ? `linear-gradient(150deg,${mixColor(tier.color, '#050B1C', 0.48)} 0%,${mixColor(
            tier.color,
            '#050B1C',
            0.8,
          )} 58%,#050A18 100%)`
        : 'linear-gradient(160deg,rgba(255,255,255,.04),rgba(255,255,255,.016))',
      cardRing: isSelected
        ? `inset 0 0 0 1.5px ${hexAlpha(tier.color, 0.8)},0 24px 54px -34px ${tier.color}`
        : 'inset 0 0 0 1px rgba(255,255,255,.07)',
      edge: isSelected
        ? `linear-gradient(90deg,${tier.color},${tier.accent},transparent)`
        : 'rgba(255,255,255,.07)',
      badgeFilter: isSelected
        ? `drop-shadow(0 6px 22px ${hexAlpha(tier.color, 0.6)})`
        : 'saturate(.32) brightness(.72) opacity(.5)',
      nameFg: isSelected ? '#fff' : 'rgba(255,255,255,.72)',
      // Todos os benefícios sempre listados; o que muda é ter check ou cadeado.
      // Benefício pendente (`pending`, ex.: saldo bônus) fica de cadeado em
      // TODOS os elos — a operação ainda não o entrega. A ordem é a mesma do
      // comparativo, sem reagrupar por liberado/cadeado: a matriz é a fonte da
      // verdade e o jogador acha a mesma linha na mesma posição nas duas telas.
      perks: benefits.map((benefit) => ({
        label: benefit.label,
        isOn: !benefit.pending && benefit.from <= tier.to,
      })),
    };
  });
});

/**
 * Rola o trilho até o cartão selecionado ficar centrado. `smooth` só depois da
 * montagem: na primeira pintura o trilho ainda está em 0, e animar dali daria
 * um arrastão de 14 cartões na entrada da página.
 */
function centerSelected(smooth = true) {
  const el = rail.value;

  if (!el) return;

  const card = el.querySelector<HTMLElement>('[data-selected="true"]');

  if (!card) return;

  el.scrollTo({
    left: card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2,
    behavior: smooth ? 'smooth' : 'auto',
  });
}

// Navegar no pódio acende o cartão certo aqui e traz ele para a vista.
watch(
  () => props.viewIndex,
  () => nextTick(centerSelected),
);

onMounted(() => centerSelected(false));

/** Avança/volta uma "página" do trilho. */
function scrollRail(direction: number) {
  const el = rail.value;

  if (!el) return;

  el.scrollBy({ left: direction * 310, behavior: 'smooth' });
}
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex items-end justify-between gap-6">
      <div class="flex flex-col gap-1.5">
        <!-- Kicker do protótipo: rótulo na cor do elo em foco + divisor que
             esmaece pra direita -->
        <div class="flex items-center gap-3">
          <span
            class="whitespace-nowrap text-[10px] font-bold uppercase leading-[13px] tracking-[0.22em]"
            :style="{ color: kickerColor }"
          >
            Escala completa
          </span>
          <span
            aria-hidden="true"
            class="h-px flex-1"
            style="
              background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 0.12),
                transparent
              );
            "
          ></span>
        </div>
        <h2
          class="text-[22px] font-extrabold leading-[27px] tracking-[-0.04em] text-white"
        >
          Os 14 elos do clube
        </h2>
        <p class="max-w-[560px] text-[13px] leading-[18px] text-white/[0.62]">
          Cada cartão mostra tudo que o elo entrega. Arraste para o lado para
          comparar.
        </p>
      </div>

      <div class="flex flex-none gap-2">
        <button
          aria-label="Voltar"
          class="flex h-[34px] w-[34px] items-center justify-center rounded-full text-white/70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
          type="button"
          @click="scrollRail(-1)"
        >
          <LevelsNavArrow
            class="h-4 w-4"
            direction="prev"
          />
        </button>
        <button
          aria-label="Avançar"
          class="flex h-[34px] w-[34px] items-center justify-center rounded-full text-white/70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
          type="button"
          @click="scrollRail(1)"
        >
          <LevelsNavArrow
            class="h-4 w-4"
            direction="next"
          />
        </button>
      </div>
    </div>

    <div
      ref="rail"
      class="levels-rail flex gap-3.5 overflow-x-auto px-0.5 pb-2.5 pt-0.5"
    >
      <button
        v-for="card in cards"
        :key="card.tier.key"
        class="transition levels-card relative w-[296px] flex-none overflow-hidden rounded-2xl px-[18px] pb-1.5 pt-[18px] text-left"
        :data-selected="card.isSelected"
        :style="{ background: card.cardBg, boxShadow: card.cardRing }"
        type="button"
        @click="emit('select', card.index)"
      >
        <span
          aria-hidden="true"
          class="transition levels-card-badge pointer-events-none absolute -right-3.5 -top-4 h-[132px] w-[132px] bg-contain bg-center bg-no-repeat"
          :style="{
            backgroundImage: `url(${card.tier.badge})`,
            filter: card.badgeFilter,
          }"
        ></span>
        <span
          aria-hidden="true"
          class="pointer-events-none absolute left-0 right-0 top-0 h-0.5"
          :style="{ background: card.edge }"
        ></span>

        <div class="relative flex min-h-[60px] flex-col gap-[9px]">
          <span
            class="whitespace-nowrap text-[21px] font-extrabold leading-[26px] tracking-[-0.035em]"
            :style="{ color: card.nameFg }"
          >
            {{ card.tier.name }}
          </span>

          <!-- Elo atual mostra a barra; os outros, um selo de estado -->
          <div
            v-if="card.state === 'current'"
            class="flex max-w-[196px] flex-col gap-[5px]"
          >
            <div class="flex items-baseline justify-between gap-2">
              <span
                class="whitespace-nowrap text-[10.5px] font-semibold leading-[14px] tracking-[0.04em] text-white/70"
              >
                Progresso no elo
              </span>
              <span
                class="whitespace-nowrap text-[11px] font-bold leading-[15px] text-white tabular-nums"
              >
                {{ card.progress }}%
              </span>
            </div>
            <span
              class="block h-[5px] overflow-hidden rounded-full bg-[rgba(0,4,16,0.6)]"
            >
              <span
                class="block h-full rounded-full"
                :style="{
                  width: `${card.progress}%`,
                  background: `linear-gradient(90deg,${card.tier.color},${card.tier.accent})`,
                }"
              ></span>
            </span>
          </div>

          <span
            v-else-if="card.state === 'done'"
            class="flex items-center gap-1.5 self-start whitespace-nowrap rounded-full px-[11px] py-[5px] text-[10.5px] font-semibold leading-[14px]"
            :style="{
              background: 'rgba(63,221,142,.14)',
              color: '#3FDD8E',
            }"
          >
            <svg
              class="block h-[11px] w-[11px] flex-none"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
              viewBox="0 0 24 24"
            >
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
            Conquistado
          </span>

          <span
            v-else
            class="flex items-center gap-[7px] self-start whitespace-nowrap rounded-full bg-white/[0.07] px-[11px] py-[5px] text-[10.5px] font-semibold leading-[14px] text-white/[0.66]"
          >
            <NuxtIcon
              class="flex-none text-[10px]"
              filled
              name="mini-games/locked"
            />
            Disponível mais tarde
          </span>
        </div>

        <!-- Todos os benefícios, com check ou cadeado -->
        <div
          class="relative -mx-[18px] mt-4 bg-[rgba(0,4,16,0.28)] px-[18px] pb-4 pt-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]"
        >
          <div class="flex flex-col gap-[9px]">
            <div
              v-for="perk in card.perks"
              :key="perk.label"
              class="flex items-center gap-[9px]"
            >
              <span
                v-if="perk.isOn"
                class="flex h-[17px] w-[17px] flex-none items-center justify-center rounded-full"
                :style="{
                  background: card.isSelected
                    ? hexAlpha(card.tier.color, 0.2)
                    : 'rgba(255,255,255,.07)',
                }"
              >
                <svg
                  class="block h-2.5 w-2.5"
                  fill="none"
                  :stroke="
                    card.isSelected
                      ? mixColor(card.tier.color, '#ffffff', 0.6)
                      : 'rgba(255,255,255,.6)'
                  "
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3.4"
                  viewBox="0 0 24 24"
                >
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </span>
              <NuxtIcon
                v-else
                class="mx-[1.5px] flex-none text-[14px] text-white/[0.34]"
                filled
                name="mini-games/locked"
              />
              <span
                class="min-w-0 flex-1 text-[12.5px] font-medium leading-[17px] tracking-[-0.005em]"
                :style="{
                  color: perk.isOn
                    ? card.isSelected
                      ? '#fff'
                      : 'rgba(255,255,255,.76)'
                    : 'rgba(255,255,255,.45)',
                }"
              >
                {{ perk.label }}
              </span>
            </div>
          </div>
        </div>
      </button>
    </div>
  </section>
</template>

<style scoped lang="postcss">
/*
  Acende/apaga do cartão selecionado. A classe `transition` fica no elemento só
  pra escapar do reset global de transition do main.scss; como o Tailwind roda
  com `important: true`, a utility de mesmo nome impõe a própria lista (sem
  `background`) a 150ms — estas regras vencem por especificidade (ver o bloco
  equivalente no TierPodium).
*/
.levels-card.transition {
  transition:
    background 0.35s ease,
    box-shadow 0.35s ease !important;
}

.levels-card-badge.transition {
  transition: filter 0.35s ease !important;
}

.levels-rail {
  scrollbar-width: none;
}

.levels-rail::-webkit-scrollbar {
  height: 0;
}
</style>
